import React from 'react';
import { ImpactCard } from '../components/ImpactCard';
import { router } from '@inertiajs/react';
import { User, Monitor, Users, ArrowUpRight } from 'lucide-react';
import momoLogo from '../../assets/images/momo-logo.png';
import nexstepLogo from '../../assets/images/nexstep.png';
import nyaLogo from '../../assets/images/NYA.jpeg';

const SupportPage: React.FC = () => {
  return (
    <div>
      <section className='py-16 text-center bg-muted'>
        <div className='container mx-auto px-6'>
          <p className='text-sm font-bold tracking-widest text-secondary uppercase mb-3 opacity-0 animate-fade-in-up'>
            Give Back
          </p>
          <h1 className='text-4xl font-bold text-foreground font-heading opacity-0 animate-fade-in-up'>
            Support Our <span className='text-primary'>Mission</span>
          </h1>
          <p
            className='text-lg text-muted-foreground mt-2 max-w-3xl mx-auto opacity-0 animate-fade-in-up'
            style={{ animationDelay: '0.2s' }}
          >
            Your contribution fuels digital literacy and empowers the next generation of tech innovators in our
            community.
          </p>
        </div>
      </section>

      <section className='py-20'>
        <div className='container mx-auto px-6'>
          <div className='grid lg:grid-cols-2 gap-12 items-center'>
            <div className='opacity-0 animate-slide-in-left' style={{ animationDelay: '0.3s' }}>
              <img
                src='https://images.unsplash.com/photo-1593113646773-028c64a8f1b8?q=80&w=2070&auto=format&fit=crop'
                alt='African student learning on a laptop'
                className='rounded-lg shadow-lg w-full h-full object-cover transform transition-transform duration-500 hover:scale-[1.01]'
              />
            </div>
            <div
              className='bg-card p-8 rounded-lg border border-border shadow-sm opacity-0 animate-slide-in-right'
              style={{ animationDelay: '0.4s' }}
            >
              <h2 className='text-2xl font-bold text-foreground mb-4 font-heading'>Support Options</h2>
              <p className='text-muted-foreground mb-8'>
                Choose your preferred method to support our mission. We accept both international and local payments.
              </p>

              <div className='space-y-10'>
                {/* PayPal Section */}
                <div>
                  <h3 className='text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4'>
                    International (Credit/Debit/PayPal)
                  </h3>
                  <div className='flex justify-center lg:justify-start'>
                    <div>
                      <style>{`
                        .pp-VMLWJVUEMVV4A {
                          text-align: center;
                          border: none;
                          border-radius: 0.25rem;
                          min-width: 11.625rem;
                          padding: 0 2rem;
                          height: 2.625rem;
                          font-weight: bold;
                          background-color: #FFD140;
                          color: #000000;
                          font-family: "Helvetica Neue", Arial, sans-serif;
                          font-size: 1rem;
                          line-height: 1.25rem;
                          cursor: pointer;
                          transition: background-color 0.2s;
                        }
                        .pp-VMLWJVUEMVV4A:hover {
                          background-color: #f5c120;
                        }
                      `}</style>
                      <form
                        action='https://www.paypal.com/ncp/payment/VMLWJVUEMVV4A'
                        method='post'
                        target='_blank'
                        style={{ display: 'inline-grid', justifyItems: 'center', alignContent: 'start', gap: '0.5rem' }}
                      >
                        <input className='pp-VMLWJVUEMVV4A' type='submit' value='Donate' />
                        <img src='https://www.paypalobjects.com/images/Debit_Credit_APM.svg' alt='cards' />
                        <section style={{ fontSize: '0.75rem' }}>
                          {' '}
                          Powered by{' '}
                          <img
                            src='https://www.paypalobjects.com/paypal-ui/logos/svg/paypal-wordmark-color.svg'
                            alt='paypal'
                            style={{ height: '0.875rem', verticalAlign: 'middle' }}
                          />
                        </section>
                      </form>
                    </div>
                  </div>
                </div>

                {/* MoMo Section */}
                <div className='pt-8 border-t border-border'>
                  <h3 className='text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4'>
                    Local (Mobile Money)
                  </h3>
                  <div className='p-5 rounded-xl bg-muted/30 border border-border/50'>
                    <div className='flex items-center gap-4 mb-6'>
                      <div className='w-12 h-12 rounded-lg bg-white flex items-center justify-center shadow-sm overflow-hidden'>
                        <img src={momoLogo} alt='MTN MoMo' className='w-full h-full object-contain' />
                      </div>
                      <div>
                        <p className='font-bold text-foreground text-lg'>NexStep Foundation</p>
                        <p className='text-sm text-muted-foreground'>MTN Mobile Money Merchant</p>
                      </div>
                    </div>

                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-6'>
                      <div className='p-3 rounded-lg bg-background/50 border border-border/30'>
                        <p className='text-[10px] text-muted-foreground uppercase tracking-widest mb-1'>Merchant ID</p>
                        <p className='font-mono font-bold text-xl text-primary'>525161</p>
                      </div>
                      <div className='p-3 rounded-lg bg-background/50 border border-border/30'>
                        <p className='text-[10px] text-muted-foreground uppercase tracking-widest mb-1'>Phone Number</p>
                        <p className='font-mono font-bold text-xl text-primary'>0241839901</p>
                      </div>
                    </div>

                    <div className='mt-5 flex items-start gap-2 text-xs text-muted-foreground'>
                      <div className='mt-0.5 rounded-full bg-primary/20 p-0.5'>
                        <div className='h-1 w-1 rounded-full bg-primary' />
                      </div>
                      <p>Transfer via regular MoMo or use Merchant ID at any agent.</p>
                    </div>
                  </div>
                </div>
              </div>

              <p className='text-[10px] text-muted-foreground text-center lg:text-left mt-8 opacity-60 italic'>
                Thank you for contributing to digital growth and inclusion.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className='py-20 bg-muted'>
        <div className='container mx-auto px-6'>
          <div className='text-center max-w-3xl mx-auto mb-12'>
            <p className='text-sm font-bold tracking-widest text-secondary uppercase mb-3'>Your Impact</p>
            <h2 className='text-3xl md:text-4xl font-bold text-foreground font-heading'>Where Your Money Goes</h2>
            <p className='text-lg text-muted-foreground mt-4'>
              Your support directly funds the programs and resources that empower our community members.
            </p>
          </div>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto'>
            <div className='opacity-0 animate-fade-in-up' style={{ animationDelay: '0.2s' }}>
              <ImpactCard
                accent='primary'
                icon={<User className='h-8 w-8' />}
                title='Sponsor a Student'
                description='Your donation can provide a student with full access to a workshop or bootcamp.'
              />
            </div>
            <div className='opacity-0 animate-fade-in-up' style={{ animationDelay: '0.3s' }}>
              <ImpactCard
                accent='secondary'
                icon={<Monitor className='h-8 w-8' />}
                title='Provide Learning Tools'
                description='Help us purchase software licenses and essential learning materials for our members.'
              />
            </div>
            <div className='opacity-0 animate-fade-in-up' style={{ animationDelay: '0.4s' }}>
              <ImpactCard
                accent='orange'
                icon={<Users className='h-8 w-8' />}
                title='Support Community Events'
                description='Help us organize meetups, hackathons, and talks that bring our community together.'
              />
            </div>
          </div>
        </div>
      </section>

      <section className='py-20'>
        <div className='container mx-auto px-6 text-center'>
          <h2 className='text-3xl md:text-4xl font-bold text-foreground mb-4 font-heading'>Proudly Partnered With</h2>
          <p className='text-lg text-muted-foreground max-w-2xl mx-auto mb-12'>
            Our work is made possible through strong collaborations. We thank our partners for their dedicated support.
          </p>
          <div
            className='grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto mb-12 text-left opacity-0 animate-fade-in'
            style={{ animationDelay: '0.5s' }}
          >
            {[
              {
                name: 'NexStep Foundation',
                logo: nexstepLogo,
                description:
                  'The parent foundation behind Digital Wave, expanding access to digital skills and opportunity across Africa.',
                website: undefined as string | undefined,
              },
              {
                name: 'National Youth Authority',
                logo: nyaLogo,
                description:
                  "Ghana's state agency facilitating youth development and empowerment nationwide.",
                website: 'https://nya.gov.gh',
              },
            ].map((partner) => (
              <div
                key={partner.name}
                className='group bg-card border border-border rounded-2xl p-6 hover:shadow-lg hover:border-primary/30 transition-all duration-300'
              >
                <div className='h-12 flex items-center mb-5'>
                  <img src={partner.logo} alt={partner.name} className='max-h-12 w-auto object-contain' />
                </div>
                <h3 className='text-lg font-bold text-foreground font-heading mb-2'>{partner.name}</h3>
                <p className='text-sm text-muted-foreground mb-4 leading-relaxed'>{partner.description}</p>
                {partner.website && (
                  <a
                    href={partner.website}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='inline-flex items-center gap-1 text-sm font-semibold text-primary hover:text-primary/80 transition-colors'
                  >
                    Visit website <ArrowUpRight className='h-4 w-4' />
                  </a>
                )}
              </div>
            ))}
          </div>
          <p className='text-muted-foreground mb-6'>Interested in becoming a partner?</p>
          <button
            onClick={() => router.visit('/contact')}
            className='font-semibold text-primary hover:text-primary/80 transition-colors inline-flex items-center group'
          >
            Contact Us to Learn More <span className='ml-1 group-hover:translate-x-1 transition-transform'>&rarr;</span>
          </button>
        </div>
      </section>
    </div>
  );
};

export default SupportPage;
